import { Layer } from '@deck.gl/core';
import { PathStyleExtension } from '@deck.gl/extensions';
import { GeoJsonLayer, PathLayer, TextLayer } from '@deck.gl/layers';
import type { MapboxOverlayProps } from '@deck.gl/mapbox';
import { MapboxOverlay } from '@deck.gl/mapbox';
import { css } from '@emotion/react';
import { useIonViewWillEnter, useIonViewWillLeave } from '@ionic/react';
import type { FeatureCollection, Polygon } from 'geojson';
import 'maplibre-gl/dist/maplibre-gl.css';
import React from 'react';
import Map, { NavigationControl, useControl, useMap } from 'react-map-gl/maplibre';
import { AirQualityMarkerProperties } from '../../common/analytics/analytics-properties/air-quality-marker';
import { AnalyticsEventName } from '../../common/analytics/types';
import { trackEvent } from '../../common/analytics/utils';
import { htmlColorToRgbArray } from '../../common/aqi';
import { MapData } from '../../common/map-data';
import { useSettings } from '../../contexts/SettingsContext';
import { type SourceType } from '../../types';
import LablededScatterPlotLayer, { MapMarkerType } from '../LabeledScatterplotLayer';
import MapMarkerDot from '../MapMarkerDot';
import MapMarkerPin from '../MapMarkerPin';
import { Spinner } from '../Spinner';
import './maplibre.override.css';

const DeckGLOverlay: React.FC<MapboxOverlayProps> = (props) => {
  const overlay = useControl(() => new MapboxOverlay(props));
  overlay.setProps(props);
  return null;
};

interface MaplibreMapProps {
  data?: MapData[];
}

const MaplibreMap: React.FC<MaplibreMapProps> = ({ data }) => {
  const { aqMap: map } = useMap();
  const {
    mapSettings,
    aqiStandard,
    marker,
    pollutantConfigs,
    networkId,
    myLocation,
    searchedLocation,
    lastSelectedLocation,
    viewport,
    setViewport,
    setDatasourceId,
  } = useSettings();

  // Force the map to be unmounted when leaving the page
  // to avoid an error with multiple map instances.
  const [showMap, setShowMap] = React.useState(true);
  useIonViewWillLeave(() => {
    setShowMap(false);
  }, []);
  useIonViewWillEnter(() => {
    setShowMap(true);
  }, []);

  // Resize the map when the view is entered
  React.useEffect(() => {
    if (showMap) {
      map?.resize();
    }
  }, [map, showMap]);

  const getRadiusBasedOnZoom = React.useCallback(
    (maxSize = 6, minSize = 3, maxZoom = 12, minZoom = 7) => {
      const zoom = viewport?.zoom ?? null;
      if (zoom === null) {
        return maxSize;
      }

      if (zoom >= maxZoom) {
        return maxSize;
      } else if (zoom <= minZoom) {
        return minSize;
      } else {
        // Linear interpolation between minSize and maxSize for zoom between minZoom and maxZoom
        return minSize + ((zoom - minZoom) * (maxSize - minSize)) / (maxZoom - minZoom);
      }
    },
    [viewport?.zoom]
  );

  const createClaritySourceLayer = React.useCallback(
    (sourceType: SourceType, data: MapData[]) => {
      if (!aqiStandard || !viewport || !pollutantConfigs) {
        return null;
      }

      return new LablededScatterPlotLayer<MapData>({
        id: sourceType,
        data: data.filter((item) => item.sourceType === sourceType),
        // map marker
        markerType: sourceType === 'CLARITY_NODE' ? MapMarkerType.Circle : MapMarkerType.Square,
        getPosition: (d: MapData) => {
          // lng, lat, z-index (to avoid overlapping)
          // z-index = 0.1 * index + 10 if the point is selected
          return [...d.position, 0.1 * d.index + (d.datasourceId === marker?.datasourceId ? 10 : 0)];
        },
        getRadius: () => getRadiusBasedOnZoom(),
        radiusUnits: 'pixels',
        getFillColor: (d) => htmlColorToRgbArray(d.getColor(aqiStandard, pollutantConfigs)), // Color based on AQI
        getLineColor: (d) => (d.datasourceId === marker?.datasourceId ? [0, 0, 0] : [255, 255, 255]),
        stroked: true, // Enable stroke (border)
        lineWidthMinPixels: 2, // Border width
        radiusScale: 2, // Increase for a larger marker size
        pickable: true,
        onClick: ({ object }) => {
          const selectedDatasourceId = object?.datasourceId;
          const selectedMarker = data.find(({ marker }) => marker.datasourceId === selectedDatasourceId)?.marker;
          const newDatasourceId =
            marker?.datasourceId === selectedMarker?.datasourceId ? null : (selectedMarker?.datasourceId ?? null);
          if (selectedMarker) {
            const eventProperties: AirQualityMarkerProperties = {
              datasourceId: selectedDatasourceId,
              sourceType: selectedMarker.sourceType,
              datasourceName: selectedMarker.datasourceName,
              sampledAt: selectedMarker.sampledAt,
              location: selectedMarker.location,
            };
            trackEvent(
              newDatasourceId ? AnalyticsEventName.SelectedMapMarker : AnalyticsEventName.ClearedMapMarker,
              eventProperties
            );
          }
          setDatasourceId(newDatasourceId);
        },
        opacity: 0.8,
        // text
        getText: (d) => d.getMainPollutantAqiDisplayValue(pollutantConfigs, aqiStandard),
        getTextSize: () => (viewport.zoom > 10 ? 11 : 0),
        getTextColor: [0, 0, 0], // Black text
        getTextAnchor: 'middle',
        getTextAlignmentBaseline: 'center',
      });
    },
    [marker, viewport?.zoom, aqiStandard, showMap, pollutantConfigs, networkId]
  );

  const createPathLayer = React.useCallback(() => {
    if (!marker || !lastSelectedLocation || !viewport) {
      return null;
    }

    const getPathWidth = () => {
      const baseWidth = 4; // Base width for the path
      const zoomFactor = 156412 / Math.pow(2, viewport.zoom); // Adjust width based on zoom level
      return baseWidth * zoomFactor;
    };

    return new PathLayer({
      id: 'path-layer',
      data: [
        {
          path: [lastSelectedLocation.coordinates, marker.location.coordinates],
          name: 'Dash Line',
        },
      ],
      getPath: (d) => d.path,
      getColor: [173, 216, 230],
      getWidth: getPathWidth,
      getDashArray: [4, 2], // Dash pattern: 4px dash, 2px gap
      getOpacity: 0.8,
      getPolygonOffset: () => [0, 0],
      extensions: [new PathStyleExtension({ dash: true })],
    });
  }, [lastSelectedLocation, marker, viewport?.zoom, showMap]);

  const createBboxLayers = React.useCallback(() => {
    if (!mapSettings?.bbox) {
      return null;
    }
    const { bbox } = mapSettings;
    const [[minLng, minLat], [maxLng, maxLat]] = bbox;
    const coordinates = [
      [
        [minLng, minLat], // Bottom-left
        [maxLng, minLat], // Bottom-right
        [maxLng, maxLat], // Top-right
        [minLng, maxLat], // Top-left
        [minLng, minLat], // Close the polygon
      ],
    ];

    const bboxLayer = new GeoJsonLayer({
      id: 'boundingBoxLayer',
      data: {
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            geometry: {
              type: 'Polygon',
              coordinates,
            },
          },
        ],
      } as FeatureCollection<Polygon>,
      stroked: true,
      lineWidthScale: 1,
      lineWidthMinPixels: 2,
      getLineColor: [255, 0, 0], // Red border
      getLineWidth: 3, // Border thickness
      getFillColor: [0, 0, 0, 0], // Transparent fill (RGBA)
    });

    const labelLayer = new TextLayer({
      id: 'boundingBoxLabelLayer',
      data: [{ position: [maxLng, maxLat], text: 'Bounding Box' }],
      getPosition: (d) => d.position,
      getText: (d) => d.text,
      getSize: 8,
      getColor: [255, 0, 0], // Red text
      getTextAnchor: 'start',
      getAlignmentBaseline: 'top',
      getPixelOffset: [5, 0],
    });

    return [bboxLayer, labelLayer];
  }, [mapSettings?.bbox]);

  const layers = React.useMemo((): Layer[] => {
    if (!data) {
      return [];
    }
    const refSiteLayer = createClaritySourceLayer('REFERENCE_SITE' as SourceType, data);
    const clarityNodeLayer = createClaritySourceLayer('CLARITY_NODE' as SourceType, data);
    const pathLayer = createPathLayer();

    const layers: (Layer | null)[] = [refSiteLayer, clarityNodeLayer, pathLayer];
    if (import.meta.env.DEV) {
      const bboxLayers = createBboxLayers();
      layers.push(...(bboxLayers ?? []));
    }
    return layers.filter((layer) => layer !== null);
  }, [data, createClaritySourceLayer, createPathLayer, createBboxLayers]);

  const maplibreMapCss = {
    spinnerBackdrop: [
      css({
        position: 'absolute',
        height: '100%',
        width: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.3)',
        zIndex: 2,
      }),
    ],
    spinnerContainer: [
      css({
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
      }),
    ],
  };

  return (
    <>
      {!data && (
        <div css={maplibreMapCss.spinnerBackdrop}>
          <div css={maplibreMapCss.spinnerContainer}>
            <Spinner type="dots" color="white" size="4rem" />
          </div>
        </div>
      )}
      {showMap && viewport && (
        <Map
          id="aqMap" // note: the tour feature uses this id
          {...viewport}
          bearing={0} // init map rotation
          pitch={0} // init map pitch
          dragRotate={false} // disable drag rotation
          keyboard={false} // disable keyboard rotation
          touchPitch={false} // disable touch pitch
          pitchWithRotate={false} // disable pitch with rotate
          onMove={(evt) => setViewport(evt.viewState)}
          mapStyle="https://basemaps.cartocdn.com/gl/voyager-gl-style/style.json"
          maxZoom={17}
        >
          <NavigationControl showCompass={false} />
          <DeckGLOverlay layers={layers} interleaved />
          {myLocation ? (
            <MapMarkerDot longitude={myLocation.coordinates[0]} latitude={myLocation.coordinates[1]} />
          ) : null}
          {searchedLocation ? (
            <MapMarkerPin longitude={searchedLocation.coordinates[0]} latitude={searchedLocation.coordinates[1]} />
          ) : null}
        </Map>
      )}
    </>
  );
};

export default MaplibreMap;
